/**
 * Module-local path math.
 *
 * Pure functions, no Three.js dependency. Returns position + tangent + yaw
 * in the module-local 3D frame after originCorrection (centerline on Z=0).
 *
 * Coordinate conventions:
 *   - Y-flip: 3D-Y = -2D-canvas-Y, so positive endAngle (2D-positive)
 *     curves toward −Y in 3D.
 *   - 1 unit = 1 mm.
 */

import type { ModuleCatalogEntry } from './ModuleCatalog.js';
import { getPointCloud } from './PointClouds.js';
import { XtsViewerErrorException } from '../types.js';
import type { Vec3 } from '../types.js';

export interface PathSample {
  /** Position in module-local 3D frame, mm. */
  positionMm: Vec3;
  /** Unit tangent in module-local 3D frame. */
  tangent: Vec3;
  /** Yaw around +Z in degrees (0 at module start). */
  yawDeg: number;
}

const X_AXIS: Vec3 = [1, 0, 0];

/** Sample point and tangent on a Straight module's centerline. */
function sampleStraight(s: number): PathSample {
  return {
    positionMm: [s, 0, 0],
    tangent: X_AXIS,
    yawDeg: 0,
  };
}

/**
 * Sample point and tangent on a Curve (analytic) module's centerline.
 * Y-flipped formulas:
 *   pfi = s / R
 *   pathPoint(s)   = ( R·sin(pfi), -sgn·R·(1 - cos(pfi)), 0 )
 *   pathTangent(s) = ( cos(pfi),   -sgn·sin(pfi),         0 )
 *   yaw_z(s)       = -sgn · pfi · 180/π   [degrees]
 */
function sampleCurve(
  s: number,
  endAngleDeg: number,
  radiusMm: number,
): PathSample {
  const sgn = Math.sign(endAngleDeg) || 1;
  const pfi = s / radiusMm;
  const cos = Math.cos(pfi);
  const sin = Math.sin(pfi);
  return {
    positionMm: [radiusMm * sin, -sgn * radiusMm * (1 - cos), 0],
    tangent: [cos, -sgn * sin, 0],
    yawDeg: (-sgn * pfi * 180) / Math.PI,
  };
}

/**
 * Sample point and tangent on a Free path (point-cloud-based).
 * Free-path sampling plus critical clamp fix at s == ModuleLength.
 */
function sampleFree(
  s: number,
  freePathId: string,
): PathSample {
  const cloud = getPointCloud(freePathId);
  if (!cloud) {
    throw new XtsViewerErrorException(
      'invalid-config',
      `Unknown point-cloud id "${freePathId}". Did you forget to register it via registerPointCloud()?`,
      { freePathId },
    );
  }
  const ds = cloud.samplingMm;
  const N = cloud.points.length;
  const idxFloat = s / ds;
  // CRITICAL: clamp to N-2 (not N-1) so P[i+1] always exists.
  const i = Math.max(0, Math.min(Math.floor(idxFloat), N - 2));
  const a = idxFloat - i;
  const p0 = cloud.points[i]!;
  const p1 = cloud.points[i + 1]!;
  const x = p0[0] + (p1[0] - p0[0]) * a;
  const y2d = p0[1] + (p1[1] - p0[1]) * a;
  const tx2d = p1[0] - p0[0];
  const ty2d = p1[1] - p0[1];
  const tlen = Math.hypot(tx2d, ty2d) || 1;
  const tx = tx2d / tlen;
  const ty2dNorm = ty2d / tlen;
  return {
    positionMm: [x, -y2d, 0],
    tangent: [tx, -ty2dNorm, 0],
    // atan2( -ty2d, tx2d ) gives the 3D yaw after Y-flip.
    yawDeg: (Math.atan2(-ty2dNorm, tx) * 180) / Math.PI,
  };
}

/**
 * Sample a module's centerline at arc length s (mm).
 * `s` is clamped to `[0, moduleLengthMm]`.
 */
export function sampleModulePath(
  module: ModuleCatalogEntry,
  sMm: number,
): PathSample {
  const s = Math.max(0, Math.min(sMm, module.moduleLengthMm));
  switch (module.pathType) {
    case 'Straight':
      return sampleStraight(s);
    case 'Curve': {
      if (module.centerlineRadiusMm == null) {
        // Catalog invariant violation — a Curve entry MUST declare its
        // radius. Throw so the bug is surfaced instead of silently
        // returning origin (which would render the mover at the part
        // start and hide the underlying data error).
        throw new XtsViewerErrorException(
          'invalid-config',
          `Curve module "${module.moduleType}" is missing centerlineRadiusMm.`,
          { moduleType: module.moduleType },
        );
      }
      return sampleCurve(s, module.endAngleDeg, module.centerlineRadiusMm);
    }
    case 'Free': {
      if (!module.freePathId) {
        throw new XtsViewerErrorException(
          'invalid-config',
          `Free module "${module.moduleType}" is missing freePathId.`,
          { moduleType: module.moduleType },
        );
      }
      return sampleFree(s, module.freePathId);
    }
  }
}

/**
 * End-of-module local transform:
 * Δ_local = T( ΔX, -ΔY_canvas, 0 ) ⊗ R_z( -θ )
 *
 * We return the position delta and yaw delta separately so callers can compose
 * them with chain.startWorldMatrix as they prefer (matrix-based or pose-based).
 */
export interface EndDelta {
  /** End-of-module position relative to startWorldMatrix, in mm. */
  deltaPositionMm: Vec3;
  /** End-of-module yaw delta in degrees (sign-flipped per Y-flip). */
  deltaYawDeg: number;
}

export function moduleEndDelta(module: ModuleCatalogEntry): EndDelta {
  switch (module.pathType) {
    case 'Straight':
      return {
        deltaPositionMm: [module.moduleLengthMm, 0, 0],
        deltaYawDeg: 0,
      };
    case 'Curve': {
      const theta = module.endAngleDeg;
      const R = module.centerlineRadiusMm!;
      const tRad = (Math.abs(theta) * Math.PI) / 180;
      const sgn = Math.sign(theta) || 1;
      // Curve end transform with analytic endpoint (overrides metadata).
      return {
        deltaPositionMm: [R * Math.sin(tRad), -sgn * R * (1 - Math.cos(tRad)), 0],
        deltaYawDeg: -theta,
      };
    }
    case 'Free': {
      // Free end transform: ΔX, ΔY = last point of point cloud.
      if (!module.freePathId) {
        throw new XtsViewerErrorException(
          'invalid-config',
          `Free module "${module.moduleType}" is missing freePathId.`,
          { moduleType: module.moduleType },
        );
      }
      const cloud = getPointCloud(module.freePathId);
      if (!cloud) {
        throw new XtsViewerErrorException(
          'invalid-config',
          `Unknown point-cloud id "${module.freePathId}" for module "${module.moduleType}".`,
          { moduleType: module.moduleType, freePathId: module.freePathId },
        );
      }
      const last = cloud.points[cloud.points.length - 1]!;
      return {
        deltaPositionMm: [last[0], -last[1], 0],
        deltaYawDeg: -module.endAngleDeg,
      };
    }
  }
}
